import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import toast from 'react-hot-toast';
import serverURL from '../config';
import { Mail, RefreshCw, ChevronDown, ChevronUp, Users } from 'lucide-react';

// ── Module-level helpers (reduce component cognitive complexity) ────────────
const buildBulkSuccessMessage = (assigned, skipped) => {
    const skippedSuffix = skipped > 0 ? `, skipped ${skipped}` : '';
    return `Assigned ${assigned} retest(s)${skippedSuffix}`;
};

const apiAssignSingle = async ({ candidateId, eventId, levelId, notify }) => {
    const token = localStorage.getItem('token');
    const endpoint = notify ? '/retest/assign-and-notify' : '/retest/assign';
    await axios.post(`${serverURL}${endpoint}`, {
        candidate_id: candidateId,
        event_id: eventId,
        level_id: levelId,
    }, { headers: { Authorization: `Bearer ${token}` } });
};

const apiBulkAssign = async ({ ids, eventId, levelId, notify }) => {
    const token = localStorage.getItem('token');
    return axios.post(`${serverURL}/retest/bulk-assign`, {
        candidate_ids: ids,
        event_id: eventId,
        level_id: levelId,
        notify,
    }, { headers: { Authorization: `Bearer ${token}` } });
};

// ── Sub-components to reduce cognitive complexity ──────────────────────────
const CandidateRow = ({ candidate, isSelected, isBusy, isThisAssigning, onToggle, onAssign }) => (
    <li
        key={candidate.candidate_id}
        className={`flex items-center justify-between gap-3 px-5 py-3 transition-colors duration-150 ${isSelected ? 'bg-red-50' : 'hover:bg-campus-bg'}`}
    >
        <label htmlFor={`candidate-${candidate.candidate_id}`} className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
            <input
                id={`candidate-${candidate.candidate_id}`}
                type="checkbox"
                checked={isSelected}
                onChange={onToggle}
                aria-label={`Select ${candidate.candidate_name}`}
                className="w-4 h-4 rounded border-campus-border text-campus-primary focus:ring-campus-primary flex-shrink-0"
            />
            <div className="min-w-0">
                <p className="text-sm font-semibold text-campus-text-primary truncate">
                    {candidate.candidate_name}
                </p>
                <p className="text-xs text-campus-text-body truncate">
                    {candidate.candidate_email}
                    {candidate.last_percentage != null && (
                        <span className="ml-2 text-campus-error font-medium">
                            {parseFloat(candidate.last_percentage).toFixed(1)}% last attempt
                        </span>
                    )}
                </p>
            </div>
        </label>
        <div className="flex items-center gap-2 flex-shrink-0">
            <button
                onClick={() => onAssign(candidate.candidate_id, false)}
                disabled={isBusy}
                title="Assign retest"
                className="p-1.5 rounded-lg border border-campus-border bg-campus-surface hover:bg-campus-bg text-campus-text-body hover:text-campus-text-primary transition-colors disabled:opacity-50"
            >
                <RefreshCw size={13} className={isThisAssigning ? 'animate-spin' : ''} />
            </button>
            <button
                onClick={() => onAssign(candidate.candidate_id, true)}
                disabled={isBusy}
                title="Assign & notify"
                className="p-1.5 rounded-lg border border-campus-border bg-campus-surface hover:bg-campus-bg text-campus-text-body hover:text-campus-text-primary transition-colors disabled:opacity-50"
            >
                <Mail size={13} className={isThisAssigning ? 'animate-bounce' : ''} />
            </button>
        </div>
    </li>
);

CandidateRow.propTypes = {
    candidate: PropTypes.shape({
        candidate_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        candidate_name: PropTypes.string,
        candidate_email: PropTypes.string,
        last_percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    isSelected: PropTypes.bool.isRequired,
    isBusy: PropTypes.bool.isRequired,
    isThisAssigning: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onAssign: PropTypes.func.isRequired,
};

// ── Component ──────────────────────────────────────────────────────────────
const RetestActionsPanel = ({ eventId, levelId, eligibleCandidates = [], onAssignmentComplete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isAssigning, setIsAssigning] = useState(null);
    const [isBulkAssigning, setIsBulkAssigning] = useState(false);

    if (!eventId || !levelId) return null;

    const hasEligible = eligibleCandidates.length > 0;
    const allSelected = hasEligible && selectedIds.length === eligibleCandidates.length;
    const isBusy = isBulkAssigning || isAssigning !== null;
    const selectedCount = selectedIds.length;
    const actionLabel = selectedCount > 0 ? `${selectedCount} Selected` : `All (${eligibleCandidates.length})`;

    const finish = () => {
        setSelectedIds([]);
        setIsOpen(false);
        if (onAssignmentComplete) onAssignmentComplete();
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? [] : eligibleCandidates.map(c => c.candidate_id));
    };

    const handleAssignSingle = async (candidateId, notify) => {
        setIsAssigning(candidateId);
        try {
            await apiAssignSingle({ candidateId, eventId, levelId, notify });
            toast.success(notify ? 'Retest assigned & email sent' : 'Retest assigned');
            finish();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to assign retest');
        } finally {
            setIsAssigning(null);
        }
    };

    const executeBulkAssign = async (notify) => {
        const ids = selectedIds.length > 0 ? selectedIds : eligibleCandidates.map(c => c.candidate_id);
        if (!ids.length) { toast.error('No candidates selected'); return; }
        setIsBulkAssigning(true);
        try {
            const res = await apiBulkAssign({ ids, eventId, levelId, notify });
            toast.success(buildBulkSuccessMessage(res.data.assigned, res.data.skipped));
            finish();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Bulk assignment failed');
        } finally {
            setIsBulkAssigning(false);
        }
    };

    const handleBulkAssign = () => executeBulkAssign(false);
    const handleBulkAssignAndNotify = () => executeBulkAssign(true);

    const headerSubtext = hasEligible
        ? `${eligibleCandidates.length} candidate(s) failed and are eligible for a retest.`
        : 'No candidates are currently eligible for a new assignment.';

    const selectAllLabel = allSelected ? 'Deselect All' : 'Select All';

    return (
        <div className="bg-campus-surface border border-campus-border rounded-card shadow-sm mb-6 overflow-hidden">
            {/* ── Header / Toggle Bar ─────────────────────────────────────────── */}
            <button
                type="button"
                onClick={() => setIsOpen(prev => !prev)}
                className={`w-full flex items-center justify-between px-5 py-4 transition-colors duration-200 ${isOpen ? 'bg-campus-bg' : 'hover:bg-campus-bg'}`}
            >
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-campus-primary">
                        <Users size={15} />
                    </div>
                    <div className="text-left">
                        <p className="text-sm font-semibold text-campus-text-primary">Retest Management</p>
                        <p className="text-xs text-campus-text-body mt-0.5">{headerSubtext}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {!isOpen && hasEligible && (
                        <div className="flex gap-2">
                            <button
                                onClick={(e) => { e.stopPropagation(); handleBulkAssign(); }}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-campus-border rounded-lg bg-campus-surface hover:bg-campus-bg text-campus-text-primary transition-colors disabled:opacity-50"
                            >
                                <RefreshCw size={12} className={isBulkAssigning ? 'animate-spin' : ''} />
                                Assign All
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleBulkAssignAndNotify(); }}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-campus-border rounded-lg bg-campus-surface hover:bg-campus-bg text-campus-text-primary transition-colors disabled:opacity-50"
                            >
                                <Mail size={12} className={isBulkAssigning ? 'animate-bounce' : ''} />
                                Assign &amp; Notify All
                            </button>
                        </div>
                    )}
                    <span className="text-campus-text-body">
                        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </span>
                </div>
            </button>

            {/* ── Accordion Body ──────────────────────────────────────────────── */}
            {isOpen && (
                <div className="border-t border-campus-border">
                    {!hasEligible ? (
                        <p className="px-5 py-6 text-sm text-center text-campus-text-body">
                            No eligible candidates to display.
                        </p>
                    ) : (
                        <>
                            {/* Toolbar row */}
                            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 bg-campus-bg border-b border-campus-border">
                                <label htmlFor="selectAllCandidates" className="flex items-center gap-2 text-sm font-medium text-campus-text-primary cursor-pointer select-none">
                                    <input
                                        id="selectAllCandidates"
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        aria-label={selectAllLabel}
                                        className="w-4 h-4 rounded border-campus-border text-campus-primary focus:ring-campus-primary"
                                    />
                                    <span>{selectAllLabel}</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={handleBulkAssign}
                                        disabled={isBusy}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium border border-campus-border rounded-lg bg-campus-surface hover:bg-campus-bg text-campus-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                    >
                                        <RefreshCw size={12} className={isBulkAssigning ? 'animate-spin' : ''} />
                                        Assign {actionLabel}
                                    </button>
                                    <button
                                        onClick={handleBulkAssignAndNotify}
                                        disabled={isBusy}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium border border-campus-border rounded-lg bg-campus-surface hover:bg-campus-bg text-campus-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                    >
                                        <Mail size={12} className={isBulkAssigning ? 'animate-bounce' : ''} />
                                        Assign &amp; Notify {actionLabel}
                                    </button>
                                </div>
                            </div>
                            {/* Candidate list */}
                            <ul className="divide-y divide-campus-border max-h-72 overflow-y-auto">
                                {eligibleCandidates.map(candidate => (
                                    <CandidateRow
                                        key={candidate.candidate_id}
                                        candidate={candidate}
                                        isSelected={selectedIds.includes(candidate.candidate_id)}
                                        isBusy={isBusy}
                                        isThisAssigning={isAssigning === candidate.candidate_id}
                                        onToggle={() => toggleSelect(candidate.candidate_id)}
                                        onAssign={handleAssignSingle}
                                    />
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

RetestActionsPanel.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    levelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eligibleCandidates: PropTypes.arrayOf(
        PropTypes.shape({
            candidate_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            candidate_name: PropTypes.string,
            candidate_email: PropTypes.string,
            last_percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
    onAssignmentComplete: PropTypes.func,
};

export default RetestActionsPanel;